import React, { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import MainLayout from '../../components/Layout'
import Button from '../../components/atoms/Buttons'
import { GET_ADMIN_SETTING } from '../../apollo/queries/adminSettings'
import { UPSERT_ADMIN_SETTING } from '../../apollo/mutations/adminSettings'
import FileSharingCard from './FileSharingCard'

const SCAN_PRICE_KEY = 'scanPriceCents'
const STRIPE_MODE_KEY = 'stripeMode'
const STRIPE_DEV_DOLLAR_KEY = 'stripeDevDollarTest'

const DevDollarRow = () => {
  const { data, loading, refetch } = useQuery(GET_ADMIN_SETTING, {
    variables: { key: STRIPE_DEV_DOLLAR_KEY },
    fetchPolicy: 'network-only',
  })
  const [upsert, { loading: saving }] = useMutation(UPSERT_ADMIN_SETTING)

  let on = false
  const item = data?.getAdminSetting
  if (item?.value) {
    try {
      on = JSON.parse(item.value) === 'on'
    } catch {
      on = item.value === 'on'
    }
  }

  const onToggle = async () => {
    await upsert({
      variables: {
        input: { key: STRIPE_DEV_DOLLAR_KEY, value: JSON.stringify(on ? 'off' : 'on') },
      },
    })
    refetch()
  }

  return (
    <div className="mt-4 pt-4 border-t border-[#EAEBED] flex items-center justify-between flex-wrap gap-3">
      <div>
        <h3 className="text-[14px] font-bold text-[#2b364b] m-0 mb-1">$1 dev checkout override</h3>
        <p className="text-[12px] text-[#6b7280] font-montserrat m-0 max-w-[440px]">
          For live smoke tests from a development machine: every checkout line item becomes{' '}
          <strong>$1</strong>. Only applies to checkouts started from a local dev site
          (localhost / private network) — customers on saddlefit.io are never affected,
          even while this is on.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={loading || saving}
        onClick={onToggle}
        className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors border-none cursor-pointer ${
          on ? 'bg-amber-500' : 'bg-gray-300'
        } ${loading || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={on ? 'Disable $1 dev checkout' : 'Enable $1 dev checkout'}
      >
        <span
          className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white transition-transform ${
            on ? 'translate-x-[27px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  )
}

const StripeModeCard = () => {
  const { data, loading, refetch } = useQuery(GET_ADMIN_SETTING, {
    variables: { key: STRIPE_MODE_KEY },
    fetchPolicy: 'network-only',
  })
  const [upsert, { loading: saving }] = useMutation(UPSERT_ADMIN_SETTING)

  const item = data?.getAdminSetting
  let mode = 'live'
  if (item?.value) {
    try {
      mode = JSON.parse(item.value) === 'test' ? 'test' : 'live'
    } catch {
      mode = item.value === 'test' ? 'test' : 'live'
    }
  }
  const isTest = mode === 'test'

  const onToggle = async () => {
    const next = isTest ? 'live' : 'test'
    const result = await Swal.fire({
      title: next === 'test' ? 'Switch Stripe to Sandbox?' : 'Switch Stripe back to Live?',
      html:
        next === 'test'
          ? 'While sandbox mode is on, <b>every checkout on saddlefit.io uses Stripe test mode</b> — real customers cannot make real payments. Use Stripe test cards (e.g. 4242&nbsp;4242&nbsp;4242&nbsp;4242). Remember to switch back when done.'
          : 'Checkouts on saddlefit.io will charge real cards again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: next === 'test' ? 'Switch to Sandbox' : 'Switch to Live',
      confirmButtonColor: next === 'test' ? '#d97706' : '#16a34a',
      cancelButtonColor: '#2b364b',
    })
    if (!result.isConfirmed) return
    await upsert({
      variables: { input: { key: STRIPE_MODE_KEY, value: JSON.stringify(next) } },
    })
    refetch()
  }

  return (
    <div className="mt-6 bg-white border border-[#EAEBED] rounded-[12px] p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[18px] font-bold text-[#2b364b] mb-1">Stripe Environment</h2>
          <p className="text-[12px] text-[#6b7280] font-montserrat m-0 max-w-[440px]">
            Switch checkout on saddlefit.io between <strong>Live</strong> (real payments) and{' '}
            <strong>Sandbox</strong> (Stripe test mode, for end-to-end testing with test cards).
            Takes effect within about 30 seconds. Applies to saddle checkout, booking deposits
            and balances, custom saddle orders, and manufacturer Stripe onboarding.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1 rounded-full text-[12px] font-semibold ${
              isTest ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
            }`}
          >
            {loading ? '…' : isTest ? 'SANDBOX (test)' : 'LIVE'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isTest}
            disabled={loading || saving}
            onClick={onToggle}
            className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors border-none cursor-pointer ${
              isTest ? 'bg-amber-500' : 'bg-green-600'
            } ${loading || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isTest ? 'Switch back to Live' : 'Switch to Sandbox'}
          >
            <span
              className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white transition-transform ${
                isTest ? 'translate-x-[27px]' : 'translate-x-[3px]'
              }`}
            />
          </button>
        </div>
      </div>
      {isTest && (
        <div className="mt-4 px-4 py-3 rounded-[8px] bg-amber-50 border border-amber-200 text-[13px] text-amber-800 font-montserrat">
          ⚠ Sandbox mode is ON — customers on saddlefit.io cannot make real payments right now.
          Test card: 4242 4242 4242 4242, any future expiry, any CVC.
        </div>
      )}
      {item?.updatedAt && (
        <div className="text-[11px] text-[#6b7280] mt-3">
          Last changed {dayjs(item.updatedAt).format('MMM D, YYYY h:mm A')}
          {item.updatedBy ? ` by ${item.updatedBy.slice(0, 8)}…` : ''}
        </div>
      )}
      <DevDollarRow />
    </div>
  )
}

const Settings = () => {
  const [priceDollars, setPriceDollars] = useState('')
  const [savedAt, setSavedAt] = useState(null)
  const [updatedBy, setUpdatedBy] = useState(null)
  const [errorText, setErrorText] = useState('')
  const [okText, setOkText] = useState('')

  const { data, loading, refetch } = useQuery(GET_ADMIN_SETTING, {
    variables: { key: SCAN_PRICE_KEY },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const item = data?.getAdminSetting
    if (item) {
      try {
        const cents = Number(JSON.parse(item.value))
        if (Number.isFinite(cents)) setPriceDollars((cents / 100).toFixed(2))
      } catch (_) {}
      setSavedAt(item.updatedAt)
      setUpdatedBy(item.updatedBy)
    }
  }, [data])

  const [upsert, { loading: saving }] = useMutation(UPSERT_ADMIN_SETTING)

  const onSave = async (e) => {
    e.preventDefault()
    setErrorText('')
    setOkText('')
    const dollars = Number(priceDollars)
    if (!Number.isFinite(dollars) || dollars < 0) {
      setErrorText('Enter a valid non-negative dollar amount.')
      return
    }
    const cents = Math.round(dollars * 100)
    try {
      await upsert({
        variables: { input: { key: SCAN_PRICE_KEY, value: JSON.stringify(cents) } },
      })
      setOkText(`Saved $${dollars.toFixed(2)}`)
      refetch()
    } catch (err) {
      setErrorText(err?.message || 'Could not save.')
    }
  }

  return (
    <MainLayout>
      <div className="w-[95%] max-w-[720px] mx-auto pt-6 pb-12">
        <h1 className="events">Settings</h1>
        <p className="text-[13px] text-[#6b7280] font-montserrat mt-1">
          Global configuration for SaddleFit operations.
        </p>

        <form onSubmit={onSave} className="mt-6 bg-white border border-[#EAEBED] rounded-[12px] p-6">
          <h2 className="text-[18px] font-bold text-[#2b364b] mb-1">Horse Scanning Price</h2>
          <p className="text-[12px] text-[#6b7280] font-montserrat mb-4">
            Total cost for a horse scan / fitting. Most events use a $50 deposit at booking and collect the
            remainder later; events marked &quot;pay in full at booking&quot; charge this entire amount at checkout.
            Changing this only affects
            <strong> new bookings</strong> — existing bookings keep their snapshotted price.
          </p>

          <label className="text-[12px] font-montserrat text-[#2b364b] uppercase tracking-wide">
            Scan Price (USD)
          </label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[20px] text-[#2b364b]">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={priceDollars}
              onChange={(e) => setPriceDollars(e.target.value)}
              disabled={loading}
              className="w-[200px] px-4 h-[44px] rounded-[8px] border border-[#c3c9d4] text-[16px]"
              placeholder="350.00"
            />
          </div>

          {errorText && <div className="text-[13px] text-[#f25022] mt-3">{errorText}</div>}
          {okText && <div className="text-[13px] text-green-700 mt-3">{okText}</div>}

          <div className="flex items-center gap-3 mt-5">
            <Button
              type="submit"
              loading={saving}
              className="text-white cursor-pointer w-[160px] h-[44px] rounded-[44px]"
              primary
            >
              Save
            </Button>
            {savedAt && (
              <div className="text-[11px] text-[#6b7280]">
                Last updated {dayjs(savedAt).format('MMM D, YYYY h:mm A')}
                {updatedBy ? ` by ${updatedBy.slice(0, 8)}…` : ''}
              </div>
            )}
          </div>
        </form>

        <StripeModeCard />

        <FileSharingCard />
      </div>
    </MainLayout>
  )
}

export default Settings
